import { Link, useNavigate } from "react-router-dom";
import { useState } from "react";
import {
  Search, User, Package, ShoppingCart, Menu, ChevronDown, MapPin, Phone, X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Sheet, SheetContent, SheetTrigger } from "../components/ui/sheet";
import { useCart } from "../context/CartContext";
import { LOGO_URL, NAV_LINKS, CATEGORIES, COMPANY } from "../data/store";

const Header = () => {
  const { count } = useCart();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const onSearch = (e) => {
    e.preventDefault();
    navigate(`/loja${query ? `?q=${encodeURIComponent(query)}` : ""}`);
  };

  return (
    <header className="sticky top-0 z-50 bg-white border-b border-slate-200">
      {/* Top bar */}
      <div className="hidden md:block bg-slate-900 text-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-9 flex items-center justify-between text-xs">
          <span className="flex items-center gap-2">
            <MapPin className="h-3.5 w-3.5 text-brand" /> Enviamos para todo o Brasil
          </span>
          <span className="flex items-center gap-2">
            <Phone className="h-3.5 w-3.5 text-brand" /> WhatsApp: {COMPANY.whatsappLabel}
          </span>
        </div>
      </div>

      {/* Main bar */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-20 flex items-center gap-4">
          {/* Mobile menu */}
          <div className="lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" data-testid="mobile-menu-button">
                  <Menu className="h-6 w-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0">
                <MobileNav />
              </SheetContent>
            </Sheet>
          </div>

          <Link to="/" data-testid="header-logo" className="shrink-0">
            <img src={LOGO_URL} alt="Sulbel" className="h-12 w-12 sm:h-14 sm:w-14 object-contain" />
          </Link>

          {/* Search */}
          <form onSubmit={onSearch} className="hidden md:flex flex-1 max-w-2xl">
            <div className="relative w-full">
              <Input
                data-testid="header-search-input"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Busque produtos, marcas ou categorias..."
                className="h-12 pr-14 rounded-md border-slate-300 text-base"
              />
              <button
                type="submit"
                data-testid="header-search-button"
                aria-label="Buscar"
                className="absolute right-1 top-1 h-10 w-12 rounded-md bg-brand hover:bg-brand-hover flex items-center justify-center transition-colors"
              >
                <Search className="h-5 w-5 text-white" />
              </button>
            </div>
          </form>

          {/* Right actions */}
          <div className="ml-auto flex items-center gap-1 sm:gap-2">
            <Link
              to="/conta"
              data-testid="header-account-link"
              className="hidden sm:flex flex-col items-start px-2 py-1 rounded-md hover:bg-slate-50 transition-colors"
            >
              <span className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
                <User className="h-5 w-5" /> Minha Conta
              </span>
            </Link>
            <Link
              to="/pedidos"
              data-testid="header-orders-link"
              className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-md text-sm font-semibold text-slate-800 hover:bg-slate-50 transition-colors"
            >
              <Package className="h-5 w-5" /> Meus Pedidos
            </Link>
            <Link
              to="/carrinho"
              data-testid="header-cart-button"
              className="relative flex items-center gap-2 px-3 py-2 rounded-md bg-slate-100 hover:bg-slate-200 transition-colors"
            >
              <div className="relative">
                <ShoppingCart className="h-5 w-5 text-slate-800" />
                {count > 0 && (
                  <span
                    data-testid="cart-count-badge"
                    className="absolute -top-2 -right-2 h-5 min-w-5 px-1 rounded-full bg-brand text-white text-[11px] font-bold flex items-center justify-center"
                  >
                    {count}
                  </span>
                )}
              </div>
              <span className="hidden sm:inline text-sm font-semibold text-slate-800">Carrinho</span>
            </Link>
          </div>
        </div>

        {/* Mobile search */}
        <form onSubmit={onSearch} className="md:hidden pb-3">
          <div className="relative w-full">
            <Input
              data-testid="header-search-input-mobile"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Busque produtos, marcas..."
              className="h-11 pr-12 rounded-md border-slate-300"
            />
            <button
              type="submit"
              aria-label="Buscar"
              className="absolute right-1 top-1 h-9 w-10 rounded-md bg-brand flex items-center justify-center"
            >
              <Search className="h-5 w-5 text-white" />
            </button>
          </div>
        </form>
      </div>

      {/* Category nav */}
      <nav className="hidden lg:block border-t border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <ul className="flex items-center gap-1 h-12">
            <li>
              <Link
                to="/loja"
                data-testid="all-categories-button"
                className="flex items-center gap-2 h-9 px-4 rounded-md bg-brand text-white text-sm font-semibold hover:bg-brand-hover transition-colors"
              >
                <Menu className="h-4 w-4" /> Todas as categorias
                <ChevronDown className="h-4 w-4" />
              </Link>
            </li>
            {NAV_LINKS.map((c) => (
              <li key={c.label}>
                <Link
                  to={c.to}
                  data-testid={`nav-${c.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="inline-flex h-9 items-center px-3 rounded-md text-sm font-medium text-slate-700 hover:text-brand hover:bg-slate-50 transition-colors"
                >
                  {c.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </nav>
    </header>
  );
};

const MobileNav = () => (
  <div className="h-full flex flex-col">
    <div className="p-4 border-b border-slate-200 flex items-center gap-3">
      <img src={LOGO_URL} alt="Sulbel" className="h-11 w-11 object-contain" />
      <span className="font-bold text-slate-900">Sulbel</span>
    </div>
    <div className="p-4 overflow-y-auto">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Navegação</p>
      <ul className="space-y-1 mb-5">
        {NAV_LINKS.slice(0, 3).map((c) => (
          <li key={c.label}>
            <Link to={c.to} className="block px-3 py-2.5 rounded-md text-slate-800 font-medium hover:bg-slate-50">
              {c.label}
            </Link>
          </li>
        ))}
      </ul>
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-2">Categorias</p>
      <ul className="space-y-1">
        {CATEGORIES.map((c) => (
          <li key={c.slug}>
            <Link
              to={`/loja?cat=${c.slug}`}
              data-testid={`mobile-cat-${c.slug}`}
              className="block px-3 py-2.5 rounded-md text-slate-700 hover:bg-slate-50"
            >
              {c.name}
            </Link>
          </li>
        ))}
      </ul>
    </div>
    <div className="mt-auto p-4 border-t border-slate-200 space-y-2">
      <Link to="/conta" className="flex items-center gap-2 text-slate-700"><User className="h-4 w-4" /> Minha Conta</Link>
      <Link to="/pedidos" className="flex items-center gap-2 text-slate-700"><Package className="h-4 w-4" /> Meus Pedidos</Link>
    </div>
  </div>
);

export default Header;
